import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IDBEmployeesSlice, IDBEmployeeTable } from 'types/db-employees.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors, formatParams } from 'utils/helpers'
import {
  IDBEmployeeContractListItem,
  IDBEmployeeDocument,
  IDBEmployeeHeader,
  IDBEmployeeNote,
  IDBEmployeeOverview,
  IDBEmployeeOverviewLastTask,
  IDBEmployeeOverviewRecentActivity,
  IDBEmployeePerformance,
  IDBEmployeeSingleContract,
} from './types'

const emptyPaginated = { data: [], totalCount: 0, totalPages: 0 }

const initialState: IDBEmployeesSlice = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  noEmployees: false,
  header: null,
  overview: null,
  overviewLastTasks: [],
  overviewRecentActivity: { ...emptyPaginated },
  performance: null,
  contract: null,
  contracts: { ...emptyPaginated },
  documents: { ...emptyPaginated },
  notes: { ...emptyPaginated },
}

export const getDBEmployees = createAsyncThunk(
  'dashboardEmployees/get',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IDBEmployeeTable>> = await baseUrl.get(
        'dashboard/employees',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeeHeader = createAsyncThunk(
  'dashboardEmployees/getHeader',
  async (params: any, thunkAPI) => {
    try {
      const { userId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IDBEmployeeHeader> = await baseUrl.get(
        `dashboard/employee/${userId}/header`,
        { params: rest },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeeOverview = createAsyncThunk(
  'dashboardEmployees/getOverview',
  async (params: any, thunkAPI) => {
    try {
      const { userId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IDBEmployeeOverview> = await baseUrl.get(
        `dashboard/employee/${userId}/overview`,
        { params: rest },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeeOverviewLastTasks = createAsyncThunk(
  'dashboardEmployees/getOverviewLastTasks',
  async (params: any, thunkAPI) => {
    try {
      const { userId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IDBEmployeeOverviewLastTask[]> = await baseUrl.get(
        `dashboard/employee/${userId}/overview/last-tasks`,
        { params: rest },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeeOverviewRecentActivity = createAsyncThunk(
  'dashboardEmployees/getOverviewRecentActivity',
  async (params: any, thunkAPI) => {
    try {
      const { userId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IDBEmployeeOverviewRecentActivity>> =
        await baseUrl.get(`dashboard/employee/${userId}/overview/recent-activity`, {
          params: rest,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeePerformance = createAsyncThunk(
  'dashboardEmployees/getPerformance',
  async (params: any, thunkAPI) => {
    try {
      const { userId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IDBEmployeePerformance> = await baseUrl.get(
        `dashboard/employee/${userId}/performance`,
        { params: rest },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeeContracts = createAsyncThunk(
  'dashboardEmployees/getContracts',
  async (params: any, thunkAPI) => {
    try {
      const { userId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IDBEmployeeContractListItem>> =
        await baseUrl.get(`dashboard/employee/${userId}/contracts`, { params: rest })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeeContract = createAsyncThunk(
  'dashboardEmployees/getContract',
  async (params: any, thunkAPI) => {
    try {
      const { userId, contractId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IDBEmployeeSingleContract> = await baseUrl.get(
        `dashboard/employee/${userId}/contracts/${contractId}`,
        { params: rest },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeeDocuments = createAsyncThunk(
  'dashboardEmployees/getDocuments',
  async (params: any, thunkAPI) => {
    try {
      const { userId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IDBEmployeeDocument>> = await baseUrl.get(
        `dashboard/employee/${userId}/documents`,
        { params: rest },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDBEmployeeNotes = createAsyncThunk(
  'dashboardEmployees/getNotes',
  async (params: any, thunkAPI) => {
    try {
      const { userId, ...rest } = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IDBEmployeeNote>> = await baseUrl.get(
        `dashboard/employee/${userId}/notes`,
        { params: rest },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

interface IAddNoteParams {
  userId: number | string
  content: string
  visibilityScope: number
  category: number
}

export const addDBEmployeeNote = createAsyncThunk(
  'dashboardEmployees/addNote',
  async (params: IAddNoteParams, thunkAPI) => {
    try {
      const { userId, content, visibilityScope, category } = params
      await baseUrl.post(`users/${userId}/notes`, { content, visibilityScope, category })
      return true
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

interface IAddDocumentParams {
  userId: number | string
  documentFile: File
  capabilityId: number
  category: number | null
  accessRestrictions: number | null
  effectiveDate: Date | null
  expiryDate: Date | null
}

export const addDBEmployeeDocument = createAsyncThunk(
  'dashboardEmployees/addDocument',
  async (params: IAddDocumentParams, thunkAPI) => {
    try {
      const {
        userId,
        documentFile,
        capabilityId,
        category,
        accessRestrictions,
        effectiveDate,
        expiryDate,
      } = params
      const documentData = {
        category,
        accessRestrictions,
        effectiveDate: effectiveDate ? effectiveDate.toISOString() : null,
        expiryDate: expiryDate ? expiryDate.toISOString() : null,
        capabilityId,
      }
      const dataResp: AxiosResponse<{ id: number }> = await baseUrl.post(
        `users/${userId}/document-data`,
        documentData,
      )
      const formData = new FormData()
      formData.append('file', documentFile)
      await baseUrl.post(`users/${dataResp.data.id}/document-file`, formData)
      return true
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const dashboardSlice = createSlice({
  name: 'dbEmployeesSlice',
  initialState,
  reducers: {
    clearDBEmployeesList: (state) => {
      state.data = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearDBEmployeeHeader: (state) => {
      state.header = null
    },
    clearDBEmployeeOverview: (state) => {
      state.overview = null
      state.overviewLastTasks = []
      state.overviewRecentActivity = { ...emptyPaginated }
    },
    clearDBEmployeePerformance: (state) => {
      state.performance = null
    },
    clearDBEmployeeContract: (state) => {
      state.contract = null
    },
    clearDBEmployeeContracts: (state) => {
      state.contracts = { ...emptyPaginated }
    },
    clearDBEmployeeDocuments: (state) => {
      state.documents = { ...emptyPaginated }
    },
    clearDBEmployeeNotes: (state) => {
      state.notes = { ...emptyPaginated }
    },
  },
  extraReducers: {
    [getDBEmployees.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDBEmployees.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IPaginatedResponse<IDBEmployeeTable> },
    ) => {
      state.data = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.noEmployees = state.data.length < 1
      state.isLoading = false
    },
    [getDBEmployees.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDBEmployeeHeader.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDBEmployeeHeader.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IDBEmployeeHeader },
    ) => {
      state.header = payload
      state.isLoading = false
    },
    [getDBEmployeeHeader.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDBEmployeeOverview.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDBEmployeeOverview.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IDBEmployeeOverview },
    ) => {
      state.overview = payload
      state.isLoading = false
    },
    [getDBEmployeeOverview.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDBEmployeeOverviewLastTasks.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IDBEmployeeOverviewLastTask[] },
    ) => {
      state.overviewLastTasks = payload
    },
    [getDBEmployeeOverviewLastTasks.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getDBEmployeeOverviewRecentActivity.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IPaginatedResponse<IDBEmployeeOverviewRecentActivity> },
    ) => {
      state.overviewRecentActivity = payload
    },
    [getDBEmployeeOverviewRecentActivity.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getDBEmployeePerformance.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDBEmployeePerformance.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IDBEmployeePerformance },
    ) => {
      state.performance = payload
      state.isLoading = false
    },
    [getDBEmployeePerformance.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDBEmployeeContract.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDBEmployeeContract.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IDBEmployeeSingleContract },
    ) => {
      state.contract = payload
      state.isLoading = false
    },
    [getDBEmployeeContract.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDBEmployeeContracts.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDBEmployeeContracts.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IPaginatedResponse<IDBEmployeeContractListItem> },
    ) => {
      state.contracts = payload
      state.isLoading = false
    },
    [getDBEmployeeContracts.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDBEmployeeDocuments.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDBEmployeeDocuments.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IPaginatedResponse<IDBEmployeeDocument> },
    ) => {
      state.documents = payload
      state.isLoading = false
    },
    [getDBEmployeeDocuments.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDBEmployeeNotes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDBEmployeeNotes.fulfilled.type]: (
      state: IDBEmployeesSlice,
      { payload }: { payload: IPaginatedResponse<IDBEmployeeNote> },
    ) => {
      state.notes = payload
      state.isLoading = false
    },
    [getDBEmployeeNotes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addDBEmployeeNote.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [addDBEmployeeDocument.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
  },
})

export const {
  clearDBEmployeesList,
  clearDBEmployeeHeader,
  clearDBEmployeeOverview,
  clearDBEmployeePerformance,
  clearDBEmployeeContract,
  clearDBEmployeeContracts,
  clearDBEmployeeDocuments,
  clearDBEmployeeNotes,
} = dashboardSlice.actions

export default dashboardSlice.reducer
